<template>
  <section
    class="public-table media-buying-ad"
    :class="{
      wavebg: loading,
    }"
  >
    <TableCard
      ref="datatable"
      v-model:sort="sorting"
      v-model:page="page"
      v-model:page-size="pageSize"
      :loading="loading"
      :columns="columns"
      :sort-by-name="sortByName"
      :sort-dir-name="sortDirName"
      :rows="rows"
      :currency="currencyLabel"
      :count="$store.state.mediaBuying.campaignsCount"
      :formaters="formaters"
      :actions="actions"
      :header="$t('mediaBuying.campaign.nested_campaigns')"
      is-static
      dont-fill-empty
      custom-context
      :disable-actions="isArchived"
      :has-pagination="true"
      placement-tooltip="bottom"
      is-row-clickable
      i18n-path="mediaBuying.channels"
      @fetch-data="fetchDataTable"
      @checked-rows="handleCheckedRows"
    >
      <template v-slot:header>
        <div
          :class="[
            'ui-d-flex',
            'ui-desktop-no-wrap',
            'ui-mobile-wrap',
            'ui-mobile-12',
          ]"
        >
          <TableCardSearchHeader
            v-model="search"
            class="ui-g-md"
            @search="handleSearch"
          />
          <UiSelect
            v-model="status"
            class="ui-align-center ui-mobile-12 ui-g-md"
            :list="statusFilters"
            :empty-values="[null]"
            width="200"
            @change="fetchData"
          />
          <div
            class="checkbox payment-history__checkbox ui-mobile-12 ui-g-md"
          >
            <input
              id="checkbox1"
              v-model="actionRequired"
              type="checkbox"
              @change="update"
            >
            <label
              class="checkbox-label checkbox-label__action-required"
              for="checkbox1"
            >
              {{ $t('mediaBuying.campaign.action_required') }}
            </label>
          </div>
        </div>
      </template>
      <template v-slot:actionsPrepend>
        <div class="ui-g-md ui-mobile-12">
          <button
            class="btn btn-primary btn-sm ui-mobile-12"
            :disabled="isArchived"
            @click="addCampaign"
          >
            + {{ $t('mediaBuying.campaigns.add_campaign') }}
          </button>
        </div>
      </template>
    </TableCard>

    <EditCampaign
      id="adversitingCampaign"
      ref="editCampaign"
      @add-campaign="fetchData"
    />
  </section>
</template>

<script>
import UrlMixin from '@/components/Query/url-mixin';
import UrlMixinHooks from '@/components/Query/url-mixin-hooks';
import InjectPlugins from '@/components/Report/Components/inject-plugins';
import { pageSizeMixin } from '@/views/Dashboard/Reports/page_size';
import EditCampaign from '@/views/Dashboard/MediaBuying/Modal/EditCampaign.vue';
import Formatters from '@/views/Dashboard/CustomReports/formaters';
import TableCard from '@/components/Cards/TableCard.vue';
import tableFlowMixinGenerator from '@/views/Dashboard/Reports/tableFlowMixin';
import TableCardSearchHeader from '@/components/Cards/TableCardSearchHeader.vue';
import formatCurrency from '@/mixins/format-currency';
import UiTableAction from '@/components/ui/UiTableAction.vue';
import UiTablePopoverAction from '@/components/ui/UiTablePopoverAction.vue';

const fieldMap = {
  post_status: 'status',
  post_type: 'type',
  // period: 'post_period_start',
  post_sum: 'amount',
  status: 'post_status',
  type: 'post_type',
  post_period_start: 'period',
  amount: 'post_sum',
};

const classes = {
  approved: 'text-green',
  on_review: 'text-orange',
  declined: 'text-red',
  paid: 'text-green',
  processing: 'text-orange',
  not_paid: 'text-red',
  for_rework: 'text-red',
  failed: 'text-red',
};

const defaultParams = {
  prop: 'created_at',
  order: 'desc',

  search: '',
  needs_attention_first: true,
  status: 'active',

  page: 1,
  pageSize: 20,
};

const tableFlowMixin = tableFlowMixinGenerator({
  defaultParams: {
    ...defaultParams,
  },
  params: Object.keys(defaultParams),
  sortByName: 'prop',
  sortDirName: 'order',
  fetchOnActivated: false,

  offset: 0,
  limit: defaultParams.pageSize,
});

export default {
  components: {
    EditCampaign,
    TableCard,
    TableCardSearchHeader,
  },
  mixins: [
    UrlMixin,
    UrlMixinHooks,
    InjectPlugins,
    pageSizeMixin,
    tableFlowMixin,
    formatCurrency,
  ],

  props: [
    'campaignName', 'statusChannel', 'statusCampaign', 'activeTab',
    'tableSorting', 'rootCampaignId', 'needsAttention', 'nestedSettingView',
  ],
  emits: ['update'],
  data() {
    return {
      status: 'active',
      actionRequired: true,
      search: '',
      loading: false,
      formaters: Formatters,
    };
  },
  computed: {
    currencyLabel() {
      return this.getCurrencyLabel(this.$store.state.mediaBuying.campaignsCurrencyCode);
    },
    columns() {
      return [
        {
          name: 'incrementing_id',
          align: 'left',
          filterable: false,
          sortable: 'custom',
          width: 60,
        },
        {
          name: 'channel_status',
          align: 'center',
          filterable: false,
          width: 90,
          renderValue: (v) => `<span class="${v === 'active' ? 'green' : 'red'}">${this.$t(
            `mediaBuying.channels.camp_status.${v}`,
          )}</span>`,
        },
        {
          name: 'campaign_name',
          align: 'left',
          minWidth: 200,
          customTooltip: true,
          renderValue: (v) => `<span class="name-bubble !tw-flex !tw-justify-between">
                              <span class="link">${v.text}</span>
                            </span>`,
        },
        {
          name: 'balance_sum_approved',
          align: 'right',
          width: 130,
          format: 'currency',
        },
        {
          name: 'balance_sum_total',
          align: 'right',
          width: 130,
          format: 'currency',
        },
        {
          name: 'paid_sum_approved',
          align: 'right',
          filterable: false,
          width: 130,
          format: 'currency',
        },
        {
          name: 'paid_sum_pending',
          align: 'right',
          width: 130,
          format: 'currency',
        },
        {
          name: 'spent_sum_approved',
          align: 'right',
          width: 130,
          format: 'currency',
        },
        {
          name: 'spent_sum_pending',
          align: 'right',
          width: 130,
          format: 'currency',
        },
        {
          name: 'registrations_count',
          align: 'right',
          format: 'number',
          width: 110,
        },
        {
          name: 'first_deposits_count',
          align: 'right',
          filterable: false,
          format: 'number',
          width: 110,
        },
        {
          name: 'deposits_sum',
          align: 'right',
          filterable: false,
          format: 'ncurrency',
          width: 110,
        },
        {
          name: 'ggr_sum',
          align: 'right',
          filterable: false,
          format: 'ncurrency',
          width: 110,
          renderValue: this.formattedCurrencyColor,
        },
        {
          name: 'ngr_sum',
          align: 'right',
          filterable: false,
          format: 'ncurrency',
          width: 110,
          renderValue: this.formattedCurrencyColor,
        },
        {
          name: 'net_project_profit_sum',
          align: 'right',
          filterable: false,
          format: 'ncurrency',
          width: 110,
          renderValue: this.formattedCurrencyColor,
        },
        {
          name: 'cost_per_player',
          align: 'right',
          filterable: false,
          format: 'currency',
          width: 110,
        },
        {
          name: 'created_at',
          align: 'left',
          filterable: false,
          sortable: 'custom',
          format: 'date-time',
          width: 150,
        },
      ];
    },

    statusFilters() {
      return [
        {
          id: '',
          text: this.$t('mediaBuying.campaigns.all_campaigns'),
        },
        {
          id: 'active',
          text: this.$t('mediaBuying.campaigns.active_campaigns'),
        },
        {
          id: 'archived',
          text: this.$t('mediaBuying.campaigns.archived_campaigns'),
        },
      ];
    },

    rows() {
      return this.$store.state.mediaBuying.campaigns.map((p) => ({
        ...p,
        channel_status: p.status,
        campaign_name: {
          text: p.name,
          count: p.needs_attention_total,
        },
      }));
    },

    isArchived() {
      return this.statusCampaign === 'archived';
    },

    actions() {
      const operations = [
        {
          name: 'show',
          componentAlt: {
            component: UiTableAction,
            props: (row) => ({
              tooltipContent: this.$t('mediaBuying.channels.show'),
              iconClass: 'far fa-eye',
              colorPreset: 'green',
              wrapperClass: 'not-mobile:!tw-hidden',
              onClick: () => {
                this.handleCheckedRows(null, null, row);
              },
            }),
          },
        },
        {
          name: 'edit',
          componentAlt: {
            component: UiTableAction,
            props: (row) => ({
              tooltipContent: this.$t('mediaBuying.channels.edit'),
              iconClass: 'far fa-pen',
              colorPreset: 'green',
              onClick: () => {
                this.editCampaign(row);
              },
            }),
          },
        },
        {
          name: 'archive',
          visible: (row) => row.status === 'active',
          componentAlt: {
            component: UiTablePopoverAction,
            props: (row) => ({
              tooltipContent: this.$t('mediaBuying.channels.archive'),
              iconClass: 'far fa-archive',
              colorPreset: 'red',
              width: 250,
              message: this.$t('mediaBuying.campaigns.archive_message'),
              buttons: [
                {
                  text: this.$t('mediaBuying.channels.cancel'),
                },
                {
                  text: this.$t('mediaBuying.channels.archive'),
                  type: 'danger',
                  click: () => this.editStatusCampaign(row),
                },
              ],
              disabled: false,
            }),
          },
        },
        {
          name: 'active',
          visible: (row) => row.status === 'archived',
          componentAlt: {
            component: UiTablePopoverAction,
            props: (row) => ({
              tooltipContent: this.$t('mediaBuying.channels.restore'),
              iconClass: 'far fa-history',
              colorPreset: 'green',
              width: 270,
              message: this.$t('mediaBuying.campaigns.restore_message'),
              buttons: [
                {
                  text: this.$t('mediaBuying.channels.cancel'),
                },
                {
                  text: this.$t('mediaBuying.channels.restore'),
                  type: 'success',
                  click: () => this.editStatusCampaign(row),
                },
              ],
            }),
          },
        },
      ];

      return {
        width: 80,
        name: this.$t('profile.apiKeys.table.actions'),
        fixed: 'right',
        operations,
      };
    },
  },
  methods: {
    formattedCurrencyColor(v) {
      if (v === null) {
        return '—';
      }

      const amount = v;
      let sum = this.$filters.currency(amount, this.currencyLabel, null, {
        symbolOnLeft: true,
        spaceBetweenAmountAndSymbol: false,
        decimalSeparator: ',',
      });
      sum = sum.includes('NaN') ? amount : sum;

      if (amount < 0) {
        return `<span class="text-red">${sum}</span>`;
      }

      if (amount > 0) {
        return `<span class="text-green">${sum}</span>`;
      }

      return `<span>${sum}</span>`;
    },

    handleCheckedRows(id, ids, row) {
      const needsAttention = this.$_.cloneDeep(this.needsAttention);
      const nestedSettingView = this.$_.cloneDeep(this.nestedSettingView);
      localStorage.setItem(
        'getBackUrlCampaign',
        JSON.stringify(this.$route.path),
      );
      this.$router.push(
        `/dashboard/media-buying-campaigns-sub/${row.id}/${this.compressUrlQuery({
          ...row,
          campaign_name: this.campaignName,
          campaign_id: row.id,
          status: '',
          needsAttention: {
            payments: row.needs_attention_payments,
            posts: row.needs_attention_posts,
            total: row.needs_attention_total,
          },
          nestedSettingView,
        })}`,
      );

      // this.update('hard');

      // document.removeEventListener('mouseup', this.listener);
    },

    addCampaign() {
      this.$refs.editCampaign.open({
        root_campaign_id: this.rootCampaignId,
        edited: false,
      });
    },

    editCampaign(row) {
      this.$refs.editCampaign.open({
        ...row,
        name: row.name,
        edited: true,
      });
    },
    addPost(row) {
      const balanceEmpty = Object.values(this.$store.state.mediaBuying.campaign.balances?.approved || {})
        .every((el) => el <= 0);

      if (!balanceEmpty || row.id) {
        this.$refs.addPost.open(row);
      } else {
        this.$alert.error(this.$t('mediaBuying.campaign.zero_balance_error'));
      }
    },
    showPost(row) {
      this.$refs.showPost.open(row);
    },

    async editStatusCampaign(row) {
      if (row.status === 'active') {
        this.$store.dispatch('postCampaignArchive', row.id)
          .then(() => {
            this.fetchData();
            this.$alert.success(
              this.$t('mediaBuying.campaigns.archive_success_message'),
            );
          })
          .catch((e) => {
            const { message } = e.response.data;

            if (message) {
              this.$alert.error(message);
            }
          });
      } else {
        await this.$store.dispatch('postCampaignActivate', row.id);
        this.fetchData();
        this.$alert.success(
          this.$t('mediaBuying.campaigns.restore_success_message'),
        );
      }
    },

    update(action) {
      this.$emit('update', {
        tab: 'campaign',
        prop: this.sorting.prop,
        order: this.sorting.order,
        action,

        needs_attention_first: this.actionRequired,
        status: this.status,
        search: this.search,

        limit: this.pageSize,
        offset: (this.page - 1) * this.pageSize,
      });
    },

    async fetchData() {
      this.update();
    },
    currencyFormatter(amount, code) {
      amount = amount.toFixed(2);
      const amountData = amount.split('.');
      const result = (+amountData[0]).toLocaleString();

      return `${this.getCurrencyLabel(code)}${result}.${amountData[1]}`;
    },
  },
  created() {
    this.actions.operations.map((e) => {
      if (e.component) {
        e.component = e.component.bind(this);
      }
    });
  },

  activated() {},

  watch: {
    activeTab() {
      if (this.activeTab === 'campaign') {
        this.update();
        // this.$store.dispatch('getPostPlatforms', {});
      }
    },
    tableSorting: {
      immediate: true,
      deep: true,
      handler(value) {
        this.sorting = {
          prop: fieldMap[value.sort_column] || value.sort_column,
          order: value.sort_dir,
        };
      },
    },
  },
};
</script>
